/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect } from "react";

import { useForm } from "react-hook-form";

import { Stack, StackItem } from "office-ui-fabric-react";
import * as Yup from "yup";

import { yupResolver } from "@hookform/resolvers/yup";

import CheckboxController from "Components/ReactHookForm/CheckboxController/CheckboxController";
import TextFieldController from "Components/ReactHookForm/TextFieldController/TextFieldController";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { OperationStatus } from "Constants/OperationStatus";
import { EMAIL_NOT_ALLOWED, INVALID_EMAIL_REG_EX } from "Constants/ValidationMessages";
import { callMsGraphGetUserByEmail } from "Utility/MsGraphApiCall";

import IUpdatePrimaryEmailPanelProps from "./IUpdatePrimaryEmailPanelProps";

export const UpdatePrimaryEmailPanel: React.FunctionComponent<IUpdatePrimaryEmailPanelProps> = (
  props: IUpdatePrimaryEmailPanelProps
) => {
  const {
    isOpen,
    setIsOpen,
    userProfileId,
    updatePrimaryEmailStatus,
    updatePrimaryEmailError,
    onPrimaryEmailUpdate,
    onPrimaryEmailUpdateReset,
    loadUserProfile,
  } = props;

  const disableActions = updatePrimaryEmailStatus === OperationStatus.Pending;

  const checkBoxText =
    "Check this box to confirm this user's Azure registration email is already updated to match the email below. A mismatch will cause the automated Azure extension to fail at the time of renewal.";

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      azureBenefitEmailChecked: false,
      newEmail: "",
    },
    resolver: yupResolver(
      Yup.object().shape({
        azureBenefitEmailChecked: Yup.bool().oneOf([true], "Please check the box to proceed"),
        newEmail: Yup.string()
          .required()
          .email("Please enter a valid email")
          .matches(INVALID_EMAIL_REG_EX, EMAIL_NOT_ALLOWED)
          .test("IsInAad", "Email is not a valid account", async (email) => {
            if (!email || !email.match(INVALID_EMAIL_REG_EX)) {
              // If email is undefined or not in the correct format, return true, do not call Graph API
              return true;
            }
            const externalEmail = `${email.replace("@", "_")}%23EXT%23@microsoft.onmicrosoft.com`;
            return await callMsGraphGetUserByEmail(externalEmail).then((value) => {
              // return !value.error; commenting this to skip GraphAPI and process the request. This will be reverted once GraphAPI is fixed.
              return true;
            });
          }),
      })
    ),
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onSaveClicked = handleSubmit((data) => {
    onPrimaryEmailUpdate && onPrimaryEmailUpdate(userProfileId, data.newEmail);
  });

  const onDismiss = (): void => {
    setIsOpen(false);
    reset();
    onPrimaryEmailUpdateReset && onPrimaryEmailUpdateReset();
  };

  useEffect(() => {
    if (updatePrimaryEmailStatus === OperationStatus.Succeeded) {
      onDismiss();
      loadUserProfile && loadUserProfile(userProfileId);
    }
  }, [updatePrimaryEmailStatus]);

  return (
    <form onSubmit={onSaveClicked}>
      <SidePanel
        isOpen={isOpen}
        title="Update Login Email"
        onDismiss={onDismiss}
        onSave={onSaveClicked}
        disableActions={disableActions}
        error={updatePrimaryEmailError}
      >
        <Stack tokens={{ childrenGap: "1rem" }}>
          <StackItem>
            <CheckboxController
              inputName="azureBenefitEmailChecked"
              control={control}
              errors={errors}
              labelDisplayName={checkBoxText}
              isDisabled={false}
            />
          </StackItem>
          <StackItem>
            <TextFieldController
              inputName="newEmail"
              control={control}
              errors={errors}
              isRequired
              hasTooltip
              labelDisplayName="Login Email"
              toolTipDisplayText="Please enter the new login email."
            />
          </StackItem>
        </Stack>
      </SidePanel>
    </form>
  );
};
